import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { MediaUploaderProps } from "./types";

export function MediaUploader({
  label,
  initialUrl = null,
  accept = "image/*",
  width = 300,
  height = 200,
  noFileText = "No file selected",
  selectedText = "Selected",
  onFileSelect,
}: MediaUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(initialUrl);

  // Determinar el tipo de media inicial basado en la URL o el accept prop
  const getInitialMediaType = () => {
    if (!initialUrl) return null;

    const urlLower = initialUrl.toLowerCase();

    // Extensiones de video - SOLO si explícitamente es video
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.mkv', '.flv', '.wmv', '.m4v'];
    const hasVideoExtension = videoExtensions.some(ext => {
      // Buscar la extensión al final de la URL o antes de query params
      return urlLower.includes(ext + '?') || urlLower.endsWith(ext);
    });

    // Solo marcar como video si tiene extensión de video Y el accept lo permite
    if (hasVideoExtension && accept.includes('video')) {
      return "video" as const;
    }

    // Por defecto SIEMPRE asumir imagen para cualquier URL
    // Esto es especialmente importante para URLs de S3 que no tienen extensión clara
    return "image" as const;
  };

  const [mediaType, setMediaType] = useState<"image" | "video" | null>(getInitialMediaType());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Actualizar preview cuando cambia initialUrl
  useEffect(() => {
    if (!file) {
      setPreview(initialUrl);
      // Usar la misma lógica que getInitialMediaType para consistencia
      const newMediaType = getInitialMediaType();
      setMediaType(newMediaType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl, file, accept]);

  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      setMediaType(file.type.startsWith("video") ? "video" : "image");
      onFileSelect(file);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else if (!initialUrl) {
      setPreview(null);
      setMediaType(null);
      onFileSelect(null);
    }
  }, [file, initialUrl, onFileSelect]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setFile(e.target.files[0]);
    }
  };

  // Función para manejar error de carga de imagen
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load image:', preview);
    // No cambiar a video automáticamente, mantener como imagen fallida
  };

  // Función para manejar error de carga de video
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.error('Failed to load video:', preview);
  };

  return (
    <div className="p-4 surface-0 border-round shadow-2 mb-4 flex flex-column align-items-center gap-3">
      <h3 className="mb-2">{label}</h3>

      <div
        className="flex justify-content-center align-items-center border-round overflow-hidden"
        style={{
          width,
          height,
          backgroundColor: "var(--surface-100)",
          border: "2px dashed var(--surface-border)",
        }}
      >
        {preview ? (
          mediaType === "video" ? (
            <video
              src={preview}
              controls
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={handleVideoError}
            />
          ) : mediaType === "image" ? (
            <img
              src={preview}
              alt="preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={handleImageError}
            />
          ) : (
            <span className="p-text-secondary text-center px-2">
              Unable to load media
            </span>
          )
        ) : (
          <span className="p-text-secondary text-center px-2">
            {noFileText}
          </span>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={onFileChange}
        style={{ display: "none" }}
      />

      <Button
        label={file ? `${selectedText}: ${file.name}` : "Choose file"}
        type="button"
        icon="pi pi-upload"
        onClick={() => fileInputRef.current?.click()}
        style={{ width }}
      />
    </div>
  );
}
